import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { appointmentsService } from '@/services/AppointmentsService'
import { companiesService } from '@/services/CompaniesService'
import { useAppStore } from '@/stores/app'
import { calendarDateKey, isAppointmentDatePast, parseAppointmentDateTime, resolveCalendarTimeZone } from '@/utils/calendarDates'

/** Asegura que active_company en localStorage tenga tenant_id (para header x-company-tenant-id) */
function ensureTenantIdInActiveCompany(companyId) {
  const active = companiesService.getActiveCompany()
  if (!companyId || (active?.tenant_id)) return
  const list = companiesService.getCompaniesFromLocal()
  const found = Array.isArray(list) ? list.find(c => c.id === companyId) : null
  if (found?.tenant_id && active?.id === companyId) {
    companiesService.setActiveCompany({ ...active, tenant_id: found.tenant_id })
  }
}

export const useAppointmentsStore = defineStore('appointments', () => {
  /** Re-evaluar citas “próximas” cuando avanza el reloj (p. ej. una cita deja de ser futura). */
  const clockTick = ref(0)
  if (typeof window !== 'undefined') {
    setInterval(() => {
      clockTick.value += 1
    }, 60_000)
  }

  // State
  const appointments = ref([])
  /** Se incrementa en cada mutación (crear/editar/cancelar/completar/reprogramar) para que quien mira disponibilidad del día sepa cuándo refrescar. */
  const dataVersion = ref(0)
  function bumpDataVersion() {
    dataVersion.value++
  }
  const isLoading = ref(false)
  const isRefreshing = ref(false)
  const error = ref(null)
  const lastFetchedAt = ref(null)
  const lastError = ref(null)
  const visibleRange = ref({ from_date: null, to_date: null })
  const selectedDate = ref(calendarDateKey(new Date(), null))

  // Working hours configuration
  const workingHours = ref({
    start: 9,
    end: 18,
    lunchBreak: {
      start: 13,
      end: 14
    },
    workingDays: [1, 2, 3, 4, 5, 6] // Monday to Saturday
  })

  // Getters
  const appointmentsByDate = computed(() => (date) => 
    appointments.value.filter(apt => apt.date === date)
  )

  function parseLocalDate(dateStr) {
  if (!dateStr) return new Date()
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

  const todayAppointments = computed(() => {
    const todayStr = calendarDateKey(new Date(), null)
    return appointments.value.filter(apt => apt.date === todayStr)
  })

  const upcomingAppointments = computed(() => {
    void clockTick.value
    const now = Date.now()

    return appointments.value
      .filter((apt) => {
        if (apt.status === 'cancelled' || apt.status === 'completed' || apt.status === 'no_show') return false
        const start = parseAppointmentDateTime(apt.date, apt.time)
        if (Number.isNaN(start.getTime())) return false
        return start.getTime() > now
      })
      .sort((a, b) => {
        const ta = parseAppointmentDateTime(a.date, a.time).getTime()
        const tb = parseAppointmentDateTime(b.date, b.time).getTime()
        return ta - tb
      })
  })

  const appointmentsByStatus = computed(() => (status) => 
    appointments.value.filter(apt => apt.status === status)
  )

  const appointmentsByClient = computed(() => (clientId) => 
    appointments.value.filter(apt => apt.clientId === clientId)
  )

  const getAppointmentById = computed(() => (id) => 
    appointments.value.find(apt => apt.id === id)
  )

  const appointmentStats = computed(() => {
    const today = calendarDateKey(new Date(), null)
    
    return {
      total: appointments.value.length,
      today: todayAppointments.value.length,
      confirmed: appointmentsByStatus.value('confirmed').length,
      pending: appointmentsByStatus.value('pending').length,
      completed: appointmentsByStatus.value('completed').length,
      cancelled: appointmentsByStatus.value('cancelled').length,
      revenue: {
        today: todayAppointments.value
          .filter(apt => apt.status === 'completed')
          .reduce((sum, apt) => sum + (parseFloat(apt.servicePrice) || 0), 0),
        total: appointments.value
          .filter(apt => apt.status === 'completed')
          .reduce((sum, apt) => sum + (parseFloat(apt.servicePrice) || 0), 0)
      }
    }
  })

  // Get available time slots for a specific date
  const getAvailableSlots = computed(() => (date, serviceDuration = 30) => {
    const dayAppointments = appointmentsByDate.value(date)
    const slots = []
    
    // Check if date is a working day
    const dayOfWeek = parseLocalDate(date).getDay()
    if (!workingHours.value.workingDays.includes(dayOfWeek)) {
      return slots
    }
    
    // Generate all possible slots
    for (let hour = workingHours.value.start; hour < workingHours.value.end; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Skip lunch break
        if (hour >= workingHours.value.lunchBreak.start && 
            hour < workingHours.value.lunchBreak.end) {
          continue
        }
        
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        const endTime = calculateEndTime(timeString, serviceDuration)
        
        // Check if slot is available
        const isAvailable = !dayAppointments.some(apt => {
          return isTimeSlotConflict(timeString, endTime, apt.time, apt.endTime)
        })
        
        if (isAvailable) {
          slots.push({
            time: timeString,
            endTime: endTime,
            available: true
          })
        }
      }
    }
    
    return slots
  })

  // Actions
  /**
   * Carga citas (GET /appointments). Opcional: status, from_date, to_date, per_page (ver AppointmentsService).
   * Sin filtros se obtienen todas las páginas para mantener estadísticas locales coherentes.
   */
  async function fetchAppointments(filters = {}) {
    if (!companiesService.hasActiveCompany()) {
      isLoading.value = false
      return
    }
    isLoading.value = true
    error.value = null
    try {
      const normalized = await appointmentsService.getAll(filters)
      mergeAppointments(normalized, filters)
      lastFetchedAt.value = new Date().toISOString()
    } catch (err) {
      error.value = 'Error al cargar citas'
      console.error('Error fetching appointments:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function refreshAppointmentsSilently(filters = {}, options = {}) {
    if (!companiesService.hasActiveCompany()) return []
    isRefreshing.value = true
    lastError.value = null
    try {
      const normalized = await appointmentsService.getAll(filters, { signal: options.signal })
      mergeAppointments(normalized, filters)
      lastFetchedAt.value = new Date().toISOString()
      return normalized
    } catch (err) {
      if (err?.name === 'AbortError') return []
      lastError.value = err
      console.warn('Silent appointments refresh failed:', err)
      return []
    } finally {
      isRefreshing.value = false
    }
  }

  function setVisibleRange(range = {}) {
    visibleRange.value = {
      from_date: range.from_date || range.start || null,
      to_date: range.to_date || range.end || null
    }
  }

  function isAppointmentInsideRange(appointment, filters = {}) {
    const from = filters.from_date || filters.start
    const to = filters.to_date || filters.end
    if (!from || !to || !appointment?.date) return true
    return appointment.date >= from && appointment.date <= to
  }

  function areAppointmentsEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
  }

  function mergeAppointments(newAppointments = [], filters = {}) {
    if (!Array.isArray(newAppointments)) return false

    const incomingById = new Map(newAppointments.map((apt) => [String(apt.id), apt]))
    let changed = false
    const next = []

    for (const existing of appointments.value) {
      const key = String(existing.id)
      const incoming = incomingById.get(key)
      if (incoming) {
        next.push(areAppointmentsEqual(existing, incoming) ? existing : incoming)
        if (!areAppointmentsEqual(existing, incoming)) changed = true
        incomingById.delete(key)
        continue
      }

      if (isAppointmentInsideRange(existing, filters)) {
        changed = true
        continue
      }

      next.push(existing)
    }

    for (const incoming of incomingById.values()) {
      next.push(incoming)
      changed = true
    }

    if (changed || next.length !== appointments.value.length) {
      appointments.value = next
    }

    return changed
  }

  async function createAppointment(companyId, appointmentData) {
    const validationErrors = validateAppointment(appointmentData)
    if (validationErrors.length > 0) {
      throw new Error(`Validation errors: ${validationErrors.join(', ')}`)
    }
    ensureTenantIdInActiveCompany(companyId)
    try {
      const result = await appointmentsService.create(companyId, appointmentData)
      const newAppointment = result.appointment
      if (newAppointment?.id) {
        appointments.value.push(newAppointment)
      }
      // Las hermanas del grupo se crearon en el mismo POST; se traen para que aparezcan ya.
      if (newAppointment?.groupMembers?.length) refreshVisibleRange()
      bumpDataVersion()
      return {
        appointment: newAppointment,
        httpStatus: result.httpStatus,
        apiSuccess: result.apiSuccess,
        apiMessage: result.apiMessage
      }
    } catch (err) {
      console.error('Error creating appointment:', err)
      throw err
    }
  }

  async function updateAppointment(id, companyId, appointmentData) {
    ensureTenantIdInActiveCompany(companyId)
    try {
      const result = await appointmentsService.update(id, companyId, appointmentData)
      const updatedAppointment = result.appointment
      const index = appointments.value.findIndex(apt => apt.id === id)
      if (index !== -1 && updatedAppointment?.id) {
        appointments.value[index] = updatedAppointment
      }
      bumpDataVersion()
      return {
        appointment: updatedAppointment,
        httpStatus: result.httpStatus,
        apiSuccess: result.apiSuccess,
        apiMessage: result.apiMessage
      }
    } catch (err) {
      console.error('Error updating appointment:', err)
      throw err
    }
  }

  async function deleteAppointment(id, options = {}) {
    try {
      await appointmentsService.delete(id, options)
      appointments.value = appointments.value.filter(apt => apt.id !== id)
      bumpDataVersion()
    } catch (err) {
      console.error('Error deleting appointment:', err)
      throw err
    }
  }

  function refreshVisibleRange() {
    const filters = Object.fromEntries(Object.entries(visibleRange.value || {}).filter(([, v]) => v))
    return refreshAppointmentsSilently(filters)
  }

  /** Cambia el estado de las citas elegidas de un grupo y refresca las visibles. */
  async function updateGroupStatus(groupId, appointmentIds, status, reason = '') {
    const response = await appointmentsService.updateGroupStatus(groupId, appointmentIds, status, reason)
    await refreshVisibleRange()
    bumpDataVersion()
    return response
  }

  /** Reprograma las citas elegidas de un grupo y refresca las visibles. */
  async function rescheduleGroup(groupId, appointmentIds, date, time) {
    const response = await appointmentsService.rescheduleGroup(groupId, appointmentIds, date, time)
    await refreshVisibleRange()
    bumpDataVersion()
    return response
  }

  /** Ids de clientes con otra cita en ese horario; no bloquea el formulario si falla. */
  async function fetchBookedClientIds(date, time, duration, excludeAppointmentId) {
    try {
      return await appointmentsService.getBookedClients(date, time, duration, excludeAppointmentId)
    } catch (err) {
      console.warn('No se pudo obtener clientes con cita en el horario:', err)
      return []
    }
  }

  async function cancelAppointment(id, reason = '') {
    try {
      await appointmentsService.updateStatus(id, 'cancelled')
      const appointment = appointments.value.find(apt => apt.id === id)
      if (appointment) {
        appointment.status = 'cancelled'
        appointment.notes = reason ? `${appointment.notes}\nCancelada: ${reason}` : appointment.notes
        appointment.updatedAt = new Date().toISOString()
      }
      bumpDataVersion()
      return appointment
    } catch (err) {
      console.error('Error cancelling appointment:', err)
      throw err
    }
  }

  async function completeAppointment(id, notes = '', tipAmount) {
    try {
      try {
        await appointmentsService.completeService(id, tipAmount)
      } catch (error) {
        if (error?.status !== 404) throw error
        await appointmentsService.updateStatus(id, 'completed', tipAmount)
      }
      const appointment = appointments.value.find(apt => apt.id === id)
      if (appointment) {
        appointment.status = 'completed'
        if (notes) {
          appointment.notes = notes
        }
        if (tipAmount != null && tipAmount !== '') {
          appointment.tipAmount = Number(tipAmount)
        }
        appointment.updatedAt = new Date().toISOString()
      }
      bumpDataVersion()
      return appointment
    } catch (err) {
      console.error('Error completando cita:', err)
      throw err
    }
  }

  async function markNoShow(id) {
    const appointment = appointments.value.find(apt => apt.id === id)
    if (!appointment) {
      throw new Error('Cita no encontrada')
    }
    try {
      await appointmentsService.updateStatus(id, 'no_show')
      appointment.status = 'no_show'
      appointment.updatedAt = new Date().toISOString()
      bumpDataVersion()
      return appointment
    } catch (err) {
      console.error('Error marcando inasistencia:', err)
      throw err
    }
  }

  function rescheduleAppointment(id, newDate, newTime) {
    const appointment = appointments.value.find(apt => apt.id === id)
    
    if (appointment) {
      // Validate new slot is available
      const endTime = calculateEndTime(newTime, appointment.duration || appointment.serviceDuration || 30)
      const conflicts = appointmentsByDate.value(newDate).filter(apt => 
        apt.id !== id && isTimeSlotConflict(newTime, endTime, apt.time, apt.endTime)
      )
      
      if (conflicts.length > 0) {
        throw new Error('El horario seleccionado no está disponible')
      }
      
      appointment.date = newDate
      appointment.time = newTime
      appointment.endTime = endTime
      appointment.updatedAt = new Date().toISOString()

      bumpDataVersion()
      return appointment
    }
    
    throw new Error('Cita no encontrada')
  }

  // Utility functions
  function calculateEndTime(startTime, durationMinutes) {
    const [hours, minutes] = startTime.split(':').map(Number)
    const startDate = new Date()
    startDate.setHours(hours, minutes, 0, 0)
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000)
    
    return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`
  }

  function isTimeSlotConflict(start1, end1, start2, end2) {
    const time1Start = timeToMinutes(start1)
    const time1End = timeToMinutes(end1)
    const time2Start = timeToMinutes(start2)
    const time2End = timeToMinutes(end2)

    if (!Number.isFinite(time1Start) || !Number.isFinite(time1End) || !Number.isFinite(time2Start) || !Number.isFinite(time2End)) {
      return false
    }
    
    return (time1Start < time2End && time1End > time2Start)
  }

  function timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number)
    return hours * 60 + minutes
  }

  // Validations
  function validateAppointment(appointmentData) {
    const errors = []
    if (!appointmentData || typeof appointmentData !== 'object') {
      errors.push('Datos de la cita no válidos')
      return errors
    }
    if (!appointmentData.clientName || appointmentData.clientName.trim().length < 2) {
      errors.push('El nombre del cliente es requerido')
    }
    
    if (!(appointmentData.clientPhone || appointmentData.phone)) {
      errors.push('El teléfono del cliente es requerido')
    }
    
    if (!appointmentData.serviceName) {
      errors.push('El servicio es requerido')
    }
    
    if (!appointmentData.date) {
      errors.push('La fecha es requerida')
    }
    
    if (!appointmentData.time) {
      errors.push('La hora es requerida')
    }
    
    // Fecha pasada según zona del negocio (horarios)
    const appStore = useAppStore()
    const tz = resolveCalendarTimeZone(appStore.workingHours?.timezone)
    if (isAppointmentDatePast(appointmentData.date, tz)) {
      errors.push('No se pueden crear citas en fechas pasadas')
    }
    
    return errors
  }

  // Set selected date
  function setSelectedDate(date) {
    selectedDate.value = date
  }

  // Reset
  function $reset() {
    appointments.value = []
    isLoading.value = false
    isRefreshing.value = false
    error.value = null
    lastFetchedAt.value = null
    lastError.value = null
    visibleRange.value = { from_date: null, to_date: null }
    selectedDate.value = calendarDateKey(new Date(), null)
  }

  return {
    // State
    appointments,
    dataVersion,
    bumpDataVersion,
    isLoading,
    isRefreshing,
    error,
    lastFetchedAt,
    lastError,
    visibleRange,
    selectedDate,
    workingHours,
    
    // Getters
    appointmentsByDate,
    todayAppointments,
    upcomingAppointments,
    appointmentsByStatus,
    appointmentsByClient,
    getAppointmentById,
    appointmentStats,
    getAvailableSlots,
    
    // Actions
    fetchAppointments,
    refreshAppointmentsSilently,
    setVisibleRange,
    mergeAppointments,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    cancelAppointment,
    updateGroupStatus,
    rescheduleGroup,
    fetchBookedClientIds,
    completeAppointment,
    markNoShow,
    rescheduleAppointment,
    validateAppointment,
    setSelectedDate,
    $reset
  }
})
